// ============================================================================
// ACCOUNT IMPORT MODAL (ACCT-CASES-001 / Phase P)
// ============================================================================
// Excel-based upsert into acct.* tables (tbl_customer, tbl_account,
// tbl_account_party, tbl_security, tbl_account_security). After a successful
// import the backend re-runs the eligibility engine inside the same tx and
// reports how many accounts became eligible.
//
// Flow:
//   1. Download template (.xlsx) — pre-populated header + sample row.
//   2. Pick a file → Upload → result panel with counts + per-row errors.
//   3. Close — caller refetches the actionable list.

import React, { useCallback, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { accountCasesService } from 'api/services/accountCasesService';
import type { AccountImportResult } from 'api/types/accountCases.types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Fired after a successful (committed) import so the parent can refresh. */
  onImported?: () => void;
}

const AccountImportModal: React.FC<Props> = ({ isOpen, onClose, onImported }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [result, setResult] = useState<AccountImportResult | null>(null);

  const reset = useCallback(() => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    reset();
    onClose();
  }, [isUploading, onClose, reset]);

  const handleDownloadTemplate = useCallback(async () => {
    setIsDownloading(true);
    try {
      await accountCasesService.downloadImportTemplate();
      notificationService.toast.success('Template downloaded.');
    } catch (err: any) {
      notificationService.toast.error(err?.message ?? 'Failed to download template.');
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const handleFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) {
      notificationService.toast.warning('Pick an .xlsx file first.');
      return;
    }
    setIsUploading(true);
    setResult(null);
    try {
      const r = await accountCasesService.importFile(file, 'strict');
      setResult(r);
      if (r.committed) {
        notificationService.toast.success(
          `Imported ${r.inserted} new + ${r.updated} updated. ${r.eligibleAccounts} became eligible.`,
        );
        if (onImported) onImported();
      } else {
        notificationService.toast.error(
          `Import rejected — ${r.errors.length} row error(s). Nothing was saved.`,
          6000,
        );
      }
    } catch (err: any) {
      const message = err?.response?.data?.message ?? err?.message ?? 'Upload failed.';
      notificationService.toast.error(message, 6000);
    } finally {
      setIsUploading(false);
    }
  }, [file, onImported]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* HEADER */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <IconSystem.importIcon size={18} style={{ verticalAlign: 'middle' }} />
            Import Account Batch
          </h2>
          <button className="modal-close" onClick={handleClose} disabled={isUploading} title="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        {/* BODY */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
          {/* Intro */}
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Upload a loan-accounts Excel (<code>.xlsx</code>) exported from your LMS. Existing accounts are
            matched on <strong>(Source System, External Ref)</strong> first, then on <strong>Account #</strong>.
            All inserts / updates run in a single transaction — any row error rolls the whole batch back.
            After a successful commit, the eligibility engine re-evaluates every imported account.
          </p>

          {/* Step 1 — Template */}
          <Section title="Step 1 — Get the template (optional)">
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button
                className="btn btn-secondary"
                onClick={handleDownloadTemplate}
                disabled={isDownloading || isUploading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <IconSystem.download size={16} />
                {isDownloading ? 'Downloading…' : 'Download Template'}
              </button>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Two sheets: <em>Instructions</em> + <em>Accounts</em> (with a sample row).
              </span>
            </div>
          </Section>

          {/* Step 2 — Upload */}
          <Section title="Step 2 — Pick a file & upload">
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFilePick}
                disabled={isUploading}
                className="form-input"
                style={{ fontSize: '0.85rem', padding: '0.375rem 0.5rem', flex: 1, minWidth: 240 }}
              />
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={!file || isUploading}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <IconSystem.upload size={16} />
                {isUploading ? 'Uploading…' : 'Upload & Import'}
              </button>
            </div>
            {file && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Selected: <strong style={{ color: 'var(--text-primary)' }}>{file.name}</strong>
                {' '}({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </Section>

          {/* Result */}
          {result && (
            <Section title="Result">
              <ResultSummary result={result} />
              {result.errors.length > 0 && <ErrorList errors={result.errors} />}
            </Section>
          )}

          {isUploading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              <div style={{
                width: '1.1rem', height: '1.1rem', border: '2px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }} />
              Processing — parsing rows, upserting in DB, re-running eligibility…
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {result && (
            <button className="btn btn-secondary" onClick={reset} disabled={isUploading}>
              <IconSystem.refresh size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Import another file
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={handleClose} disabled={isUploading}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{
    border: '1px solid var(--border-color)', borderRadius: 8, padding: '0.9rem 1rem',
    background: 'var(--bg-primary)', marginBottom: '0.85rem',
  }}>
    <div style={{
      fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
      color: 'var(--text-secondary)', marginBottom: '0.6rem',
    }}>
      {title}
    </div>
    {children}
  </div>
);

const ResultSummary: React.FC<{ result: AccountImportResult }> = ({ result }) => {
  const ok = result.committed;
  return (
    <div>
      <div style={{
        padding: '0.55rem 0.75rem', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600,
        marginBottom: '0.75rem',
        background: ok ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.10)',
        color: ok ? '#15803d' : '#b91c1c',
        border: `1px solid ${ok ? '#16a34a' : '#dc2626'}`,
      }}>
        {ok ? 'Committed — all rows saved.' : 'Rejected — nothing was saved.'}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.6rem',
      }}>
        <Stat label="Total Rows"       value={result.totalRows} />
        <Stat label="Inserted"         value={result.inserted}        color="#16a34a" />
        <Stat label="Updated"          value={result.updated}         color="#2563eb" />
        <Stat label="Skipped"          value={result.skipped}         color="#6b7280" />
        <Stat label="Errors"           value={result.errors.length}   color={result.errors.length ? '#dc2626' : '#6b7280'} />
        <Stat label="Eligible (new)"   value={result.eligibleAccounts} color="#d97706" />
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <div style={{
    border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.5rem 0.6rem',
    background: 'var(--bg-secondary)',
  }}>
    <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: color ?? 'var(--text-primary)' }}>{value}</div>
  </div>
);

const ErrorList: React.FC<{ errors: AccountImportResult['errors'] }> = ({ errors }) => (
  <div style={{ marginTop: '0.85rem' }}>
    <div style={{
      fontSize: '0.78rem', fontWeight: 600, color: '#b91c1c', marginBottom: '0.45rem',
    }}>
      Row errors ({errors.length})
    </div>
    <div style={{
      maxHeight: 220, overflowY: 'auto',
      border: '1px solid var(--border-color)', borderRadius: 6,
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
        <thead>
          <tr style={{ background: 'var(--bg-secondary)' }}>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Account #</th>
            <th style={thStyle}>Reason</th>
          </tr>
        </thead>
        <tbody>
          {errors.map((e, idx) => (
            <tr key={`${e.rowNumber}-${idx}`} style={{ borderTop: '1px solid var(--border-color)' }}>
              <td style={tdStyle}>{e.rowNumber}</td>
              <td style={tdStyle}>{e.accountNumber ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
              <td style={{ ...tdStyle, color: '#b91c1c' }}>{e.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '0.4rem 0.6rem', fontWeight: 600,
  color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)',
};
const tdStyle: React.CSSProperties = { padding: '0.4rem 0.6rem', verticalAlign: 'top' };

export default AccountImportModal;
